// Tenant map — the response turned into one graph the three views share.
//
// One node per unique thing. Carriers (agents, repos) sit on the left; the
// resources they carry (skills, tools, knowledge bases, models) on the right.
// An edge is a carry; a skill edge is a "variant" when that carrier's copy is
// not the canonical one (the server decides which copy is canonical).

import type { MapSource, TenantMap } from "@/types/api";

export type Kind = "agent" | "repo" | "skill" | "tool" | "kb" | "model";
export type ResourceKind = Exclude<Kind, "agent" | "repo">;

export interface MapNode {
  id: string;
  kind: Kind;
  name: string;
  source: MapSource;
  /** Agents only: the domain they sit under. */
  domainId?: number;
  /** One line of detail: a path, a version, a provider. */
  sub: string;
  /** Number of edges — for a resource, how many carriers hold it. */
  deg: number;
}

export interface MapEdge {
  a: string;
  b: string;
  variant: boolean;
}

export interface Graph {
  nodes: Map<string, MapNode>;
  edges: MapEdge[];
  /** id -> its neighbours, with whether that edge is a variant. */
  adj: Map<string, { id: string; variant: boolean }[]>;
  data: TenantMap;
}

export const KIND_LABEL: Record<Kind, string> = {
  agent: "Agent",
  repo: "GitHub repo",
  skill: "Skill",
  tool: "Tool",
  kb: "Knowledge base",
  model: "LLM provider",
};

export const SOURCE_LABEL: Record<MapSource, string> = {
  aviary: "Aviary",
  claude: "Claude Code",
  cursor: "Cursor",
};

export const isCarrier = (k: Kind) => k === "agent" || k === "repo";

export function buildGraph(data: TenantMap): Graph {
  const nodes = new Map<string, MapNode>();
  const edges: MapEdge[] = [];
  const add = (n: Omit<MapNode, "deg">) => nodes.set(n.id, { ...n, deg: 0 });

  data.agents.forEach((a) =>
    add({
      id: `agent:${a.id}`,
      kind: "agent",
      name: a.name,
      source: "aviary",
      domainId: a.domain_id,
      sub: a.version ? `${a.slug} · v${a.version}` : a.slug,
    }),
  );
  data.repos.forEach((r) =>
    add({
      id: `repo:${r.id}`,
      kind: "repo",
      name: r.full_name,
      source: "claude",
      sub: `github.com/${r.full_name} · ${r.default_branch}`,
    }),
  );
  data.skills.forEach((s) =>
    add({
      id: `skill:${s.key}`,
      kind: "skill",
      name: s.name,
      source: s.source,
      sub: [...new Set(s.copies.filter((c) => c.source !== "aviary").map((c) => c.path))].join(
        ", ",
      ),
    }),
  );
  data.tools.forEach((t) =>
    add({ id: `tool:${t.id}`, kind: "tool", name: t.name, source: "aviary", sub: t.tool_name }),
  );
  data.knowledge_bases.forEach((k) =>
    add({
      id: `kb:${k.id}`,
      kind: "kb",
      name: k.name,
      source: "aviary",
      sub: `${k.documents} document${k.documents === 1 ? "" : "s"}`,
    }),
  );
  data.models.forEach((m) =>
    add({
      id: `model:${m.id}`,
      kind: "model",
      name: m.name,
      source: "aviary",
      sub: `${m.provider} · ${m.model}`,
    }),
  );

  const link = (a: string, b: string, variant = false) => {
    const na = nodes.get(a);
    const nb = nodes.get(b);
    if (!na || !nb) return; // e.g. a model the API no longer lists
    edges.push({ a, b, variant });
    na.deg++;
    nb.deg++;
  };
  data.agents.forEach((a) => {
    const id = `agent:${a.id}`;
    a.skills.forEach((s) => link(id, `skill:${s.key}`, s.differs));
    a.tool_ids.forEach((t) => link(id, `tool:${t}`));
    a.kb_ids.forEach((k) => link(id, `kb:${k}`));
    if (a.model_id !== null) link(id, `model:${a.model_id}`);
  });
  data.repos.forEach((r) =>
    r.skills.forEach((s) => link(`repo:${r.id}`, `skill:${s.key}`, s.differs)),
  );

  const adj = new Map<string, { id: string; variant: boolean }[]>();
  nodes.forEach((_, id) => adj.set(id, []));
  edges.forEach((e) => {
    adj.get(e.a)!.push({ id: e.b, variant: e.variant });
    adj.get(e.b)!.push({ id: e.a, variant: e.variant });
  });
  return { nodes, edges, adj, data };
}

export const nodesOf = (g: Graph, kind: Kind) =>
  [...g.nodes.values()].filter((n) => n.kind === kind);

export const neighboursOf = (g: Graph, id: string, kind?: Kind) =>
  (g.adj.get(id) ?? []).filter((x) => !kind || g.nodes.get(x.id)?.kind === kind);

/** A resource some carrier holds a differing copy of. */
export const hasDrift = (g: Graph, id: string) => neighboursOf(g, id).some((x) => x.variant);

// ---- similarity ordering ------------------------------------------------------

export function jaccard(a: string[], b: string[]): number {
  const A = new Set(a);
  const B = new Set(b);
  let both = 0;
  A.forEach((x) => {
    if (B.has(x)) both++;
  });
  const union = A.size + B.size - both;
  return union ? both / union : 1;
}

/** Greedy nearest-neighbour chain from the largest set, so items with similar
 *  sets end up next to each other and clusters surface on their own. */
export function orderBySimilarity<T>(items: T[], setOf: (t: T) => string[]): T[] {
  const left = [...items].sort((x, y) => setOf(y).length - setOf(x).length);
  const out: T[] = left.splice(0, 1);
  let last = out[0];
  while (last !== undefined && left.length) {
    const lastSet = setOf(last);
    let best = 0;
    let bestScore = -1;
    left.forEach((it, i) => {
      const s = jaccard(lastSet, setOf(it));
      if (s > bestScore) {
        bestScore = s;
        best = i;
      }
    });
    out.push(...left.splice(best, 1));
    last = out[out.length - 1];
  }
  return out;
}

// ---- findings ---------------------------------------------------------------------

export interface Finding {
  title: string;
  tone: "drift" | "orphan" | "shared" | "plain";
  count?: number;
  /** Names to show as code, then the sentence after them. */
  items: string[];
  text: string;
}

/** What the data says, computed rather than written by hand. */
export function findings(g: Graph): Finding[] {
  const out: Finding[] = [];
  const skills = nodesOf(g, "skill");

  const drifted = skills.filter((s) => hasDrift(g, s.id));
  const variants = new Map(g.data.skills.map((s) => [`skill:${s.key}`, s.variants]));
  out.push({
    title: "Skills with content drift",
    tone: "drift",
    count: drifted.length,
    items: drifted.map((s) => `${s.name} (${variants.get(s.id)} variants)`),
    text: drifted.length
      ? "Same name, different content. The copy most carriers share is treated as canonical; bring the others in line or rename them."
      : "Every shared skill has the same content wherever it is carried.",
  });

  const orphans = skills.filter((s) => s.deg <= 1);
  out.push({
    title: "Orphan skills",
    tone: "orphan",
    count: orphans.length,
    items: orphans.map((s) => s.name),
    text: orphans.length
      ? "Exist in one place only. Candidates to share more widely or retire."
      : "None: every skill is carried in at least two places.",
  });

  const platform = skills.filter((s) => s.deg >= 4).sort((a, b) => b.deg - a.deg);
  out.push({
    title: "De facto platform skills",
    tone: "shared",
    count: platform.length,
    items: platform.map((s) => `${s.name} ×${s.deg}`),
    text: platform.length
      ? "Carried by four or more agents and repos; the ones worth versioning centrally."
      : "No skill is carried by four or more agents and repos yet.",
  });

  const signature = (a: TenantMap["agents"][number]) =>
    [...a.skills.map((s) => s.key)].sort().join("|") + "#" + [...a.tool_ids].sort().join("|");
  const loadouts = new Map<string, string[]>();
  g.data.agents
    .filter((a) => a.skills.length || a.tool_ids.length)
    .forEach((a) => loadouts.set(signature(a), [...(loadouts.get(signature(a)) ?? []), a.name]));
  const twins = [...loadouts.values()].filter((names) => names.length > 1);
  out.push({
    title: "Identical loadouts",
    tone: "plain",
    items: twins.map((names) => names.join(" and ")),
    text: twins.length
      ? "Carry exactly the same skills and tools. Either one is redundant or the difference lives only in the prompt, which the map cannot see."
      : "No two agents share an identical skill and tool loadout.",
  });

  const unused = (["tool", "kb", "model"] as const).flatMap((k) =>
    nodesOf(g, k).filter((n) => n.deg === 0),
  );
  out.push({
    title: "Not used by any agent",
    tone: "plain",
    count: unused.length,
    items: unused.map((n) => `${n.name} (${KIND_LABEL[n.kind].toLowerCase()})`),
    text: unused.length
      ? "Configured but bound to no agent's current version."
      : "Every tool, knowledge base and model is used by at least one agent.",
  });

  const tools = nodesOf(g, "tool").sort((a, b) => b.deg - a.deg);
  const busiest = tools[0];
  if (busiest && busiest.deg > 0) {
    out.push({
      title: "Tool concentration",
      tone: "plain",
      items: [busiest.name],
      text: `is bound to ${busiest.deg} of ${g.data.agents.length} agents; ${
        tools.filter((t) => t.deg <= 1).length
      } tools are used by one agent or none.`,
    });
  }
  return out;
}
